import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "thanksgiving-gratitude-exchanges",
  "title": "Thanksgiving Gratitude Exchanges: Gifts of Appreciation",
  "excerpt": "Beyond the turkey: organize meaningful Thanksgiving gift exchanges that express gratitude and strengthen family bonds.",
  "date": "2024-11-10",
  "readTime": "5 min read",
  "category": "Festivals",
  "image": "🦃",
  "heroImage": "https://images.unsplash.com/photo-1574672280600-4accfa5b6f98?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Thanksgiving Gratitude Exchanges: Gifts of Appreciation\n\nThanksgiving is about gratitude, and adding a gift exchange can deepen connections and create lasting memories.\n\n## Gratitude-Focused Exchange Ideas\n\n**Thankful for You Exchange**:\n- Each person draws a name\n- Write a heartfelt letter expressing why you're thankful for them\n- Pair it with a small, meaningful gift ($10-20)\n\n**Memory Gift Exchange**:\n- Give a gift that represents a favorite shared memory\n- Include a card explaining the significance\n- Budget: $15-30\n\n## Traditional Thanksgiving Gifts\n\n**For Hosts**:\n- Wine or specialty beverages\n- Gourmet food items\n- Serving dishes or platters\n- Decorative centerpieces\n- Homemade pie or dessert\n\n**Hostess Gift Ideas** ($20-50):\n- Nice bottle of wine\n- Decorative serving bowl\n- Gourmet cheese and crackers\n- Scented candles\n- Kitchen towels and accessories\n- Coffee table book\n\n## Family Exchange Activities\n\n**The Gratitude Game**:\n- Everyone brings a wrapped gift ($20 budget)\n- Take turns sharing what you're grateful for\n- After each share, choose or steal a gift\n- Similar to White Elephant but with gratitude focus\n\n**Thankful Tree**:\n- Create a tree branch centerpiece\n- Attach small gifts or gift cards to branches\n- Each person picks one while sharing gratitude\n\n## Kids' Thanksgiving Exchange\n\n**Cousins' Gift Swap**:\n- Age-appropriate budget ($10-15)\n- Focus on games, books, or crafts\n- Draw names early in November\n- Exchange before or after Thanksgiving meal\n\n**Handmade Gift Exchange**:\n- Kids create gifts for each other\n- Crafts, drawings, baked goods\n- Emphasizes effort and thoughtfulness\n\n## Charitable Thanksgiving Traditions\n\n**Giving Back**:\n- Skip family gifts, donate to food bank instead\n- Volunteer together at a soup kitchen\n- \"Adopt\" a family for Thanksgiving meal\n- Donate to causes each family member cares about\n\n**Alternative Gift Exchange**:\n- Each person donates in another's name\n- Share at dinner what cause you chose and why\n- Creates meaningful conversation\n\n## Budget-Friendly Ideas\n\nUnder $20:\n- Homemade baked goods\n- Seasonal potpourri or candles\n- Fall-themed kitchen items\n- Gratitude journal\n- Photo in a nice frame\n- Specialty coffee or tea\n- Cookbook with a favorite recipe marked\n\n## Organizing Tips\n\n**For Large Families**:\n1. Start planning in October\n2. Set clear budget and date\n3. Decide on exchange format\n4. Use SwapGifts to draw names\n5. Send reminders two weeks before\n6. Plan the exchange timing during gathering\n\n**Timing Options**:\n- Before dinner (builds anticipation)\n- Between dinner and dessert\n- After dessert (ending on a high note)\n- Following day (if overnight guests)\n\n## Making It Meaningful\n\n**Include Personal Touches**:\n- Handwritten notes\n- Share why you're grateful for the recipient\n- Reference shared memories\n- Include family photos\n\n**Create Traditions**:\n- Same exchange format every year\n- Rotating host duties\n- Special wrapping paper used only for Thanksgiving\n- Photo of everyone with their gifts\n\n## Conclusion\n\nThanksgiving gift exchanges, when focused on gratitude and connection rather than material value, create cherished traditions that strengthen family bonds for generations."
};

export default function ThanksgivingGratitudeExchangesPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
